/**
 * MonthlyBudgetReport — Printable 50/30/20 budget breakdown
 *
 * Shows income, total spent, remaining, per-bucket status with progress bars,
 * and category-level breakdown grouped by bucket. Replicates BudgetPage
 * aggregation logic for self-contained report rendering.
 *
 * Phase 9A
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserConfig, getTransactionsByMonth, getCategoryOverrides,
} from '../../services/db';
import { calculateBudget, BUCKETS, BUCKET_META, getCategoryBucket } from '../../utils/budget';
import { formatCurrency, formatDate, getMonthKey, shiftMonthKey, formatMonthLabel } from '../../utils/format';
import theme from '../../utils/theme';

export default function MonthlyBudgetReport() {
  const { user } = useAuth();
  const [monthKey, setMonthKey] = useState(getMonthKey());
  const [transactions, setTransactions] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [income, setIncome] = useState(8030);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [config, txns, cats] = await Promise.all([
        getUserConfig(user.id),
        getTransactionsByMonth(user.id, monthKey),
        getCategoryOverrides(user.id),
      ]);
      setIncome(config?.conservative_income || 8030);
      setTransactions(txns);
      setOverrides(cats);
    } catch (err) {
      console.error('Failed to load budget report:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id, monthKey]);

  useEffect(() => { loadData(); }, [loadData]);

  // Aggregate transactions by category (same as BudgetPage)
  const aggregated = useMemo(() => {
    const categoryMap = {};
    let needTotal = 0, wantTotal = 0, savingsTotal = 0, incomeTotal = 0;

    for (const txn of transactions) {
      const cat = txn.category || 'Uncategorized';
      const bucket = txn.bucket || getCategoryBucket(cat, overrides);

      if (bucket === 'income' || txn.is_credit) {
        incomeTotal += txn.amount || 0;
        continue;
      }

      if (!categoryMap[cat]) {
        categoryMap[cat] = { amount: 0, count: 0, bucket };
      }
      categoryMap[cat].amount += txn.amount || 0;
      categoryMap[cat].count += 1;
      categoryMap[cat].bucket = bucket;

      if (bucket === 'need') needTotal += txn.amount || 0;
      else if (bucket === 'savings') savingsTotal += txn.amount || 0;
      else wantTotal += txn.amount || 0;
    }

    return { categoryMap, needTotal, wantTotal, savingsTotal, incomeTotal };
  }, [transactions, overrides]);

  const budgetData = useMemo(() => calculateBudget({
    income,
    needActual: aggregated.needTotal,
    wantActual: aggregated.wantTotal,
    savingsActual: aggregated.savingsTotal,
  }), [income, aggregated]);

  // Group categories by bucket
  const categoryGroups = useMemo(() => {
    const groups = { need: [], want: [], savings: [] };
    for (const [cat, data] of Object.entries(aggregated.categoryMap)) {
      const bucket = data.bucket;
      if (groups[bucket]) {
        groups[bucket].push({ name: cat, ...data });
      } else {
        groups.want.push({ name: cat, ...data });
      }
    }
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => b.amount - a.amount);
    }
    return groups;
  }, [aggregated]);

  if (loading) {
    return <div className="report-content"><p className="empty-state">Loading report...</p></div>;
  }

  const totalSpent = aggregated.needTotal + aggregated.wantTotal;
  const remaining = income - totalSpent;

  return (
    <div className="report-content">
      {/* Print-only header */}
      <div className="report-header">
        <div className="report-header-title">Spendable — Monthly Budget</div>
        <div className="report-header-subtitle">{formatMonthLabel(monthKey)}</div>
      </div>

      {/* Month selector (hidden in print) */}
      <div className="month-selector">
        <button onClick={() => setMonthKey(k => shiftMonthKey(k, -1))}>&lsaquo;</button>
        <span className="month-label">{formatMonthLabel(monthKey)}</span>
        <button onClick={() => setMonthKey(k => shiftMonthKey(k, 1))}>&rsaquo;</button>
      </div>

      {/* Summary */}
      <div className="report-section">
        <div className="report-section-title">Summary</div>
        <div className="report-summary-row">
          <span className="report-summary-label">Income (budget)</span>
          <span className="report-summary-value">{formatCurrency(income)}</span>
        </div>
        <div className="report-summary-row">
          <span className="report-summary-label">Total Spent</span>
          <span className="report-summary-value">{formatCurrency(totalSpent)}</span>
        </div>
        <div className="report-summary-row">
          <span className="report-summary-label">Remaining</span>
          <span className="report-summary-value" style={{ color: remaining >= 0 ? theme.success : theme.danger }}>
            {formatCurrency(remaining)}
          </span>
        </div>
      </div>

      {/* 50/30/20 breakdown */}
      <div className="report-section">
        <div className="report-section-title">50/30/20 Budget</div>
        <table>
          <thead>
            <tr>
              <th>Bucket</th>
              <th className="text-right">Target</th>
              <th className="text-right">Actual</th>
              <th className="text-right">Remaining</th>
              <th style={{ width: '80px' }}>%</th>
            </tr>
          </thead>
          <tbody>
            {[BUCKETS.NEED, BUCKETS.WANT, BUCKETS.SAVINGS].map(bucket => {
              const b = budgetData.buckets[bucket];
              const meta = BUCKET_META[bucket];
              const pct = Math.min(b.percent, 100);
              const color = b.status === 'on-track' ? theme.success : b.status === 'warning' ? theme.warning : theme.danger;
              return (
                <tr key={bucket}>
                  <td style={{ fontWeight: 600 }}>{meta.emoji} {meta.label}</td>
                  <td className="text-right">{formatCurrency(b.target, false)}</td>
                  <td className="text-right">{formatCurrency(b.actual, false)}</td>
                  <td className="text-right" style={{ color: b.remaining >= 0 ? theme.success : theme.danger }}>
                    {formatCurrency(b.remaining, false)}
                  </td>
                  <td>
                    <div className="report-bar">
                      <div className="report-bar-fill" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Category breakdown by bucket */}
      {[BUCKETS.NEED, BUCKETS.WANT, BUCKETS.SAVINGS].map(bucket => {
        const meta = BUCKET_META[bucket];
        const cats = categoryGroups[bucket];
        if (cats.length === 0) return null;
        const bucketTotal = cats.reduce((s, c) => s + c.amount, 0);

        return (
          <div key={bucket} className="report-section">
            <div className="report-section-title" style={{ color: meta.color }}>
              {meta.emoji} {meta.label} — {formatCurrency(bucketTotal, false)}
            </div>
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">% of Bucket</th>
                  <th style={{ width: '60px' }}></th>
                </tr>
              </thead>
              <tbody>
                {cats.map(cat => {
                  const pctOfBucket = bucketTotal > 0 ? (cat.amount / bucketTotal) * 100 : 0;
                  return (
                    <tr key={cat.name}>
                      <td>{cat.name}</td>
                      <td className="text-right">{formatCurrency(cat.amount)}</td>
                      <td className="text-right">{pctOfBucket.toFixed(0)}%</td>
                      <td>
                        <div className="report-bar">
                          <div className="report-bar-fill" style={{
                            width: `${Math.min(pctOfBucket, 100)}%`,
                            background: meta.color,
                          }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {Object.keys(aggregated.categoryMap).length === 0 && (
        <p className="empty-state">No spending data for this month.</p>
      )}

      <div className="report-footer">
        Generated {formatDate(new Date(), 'full')}
      </div>
    </div>
  );
}
